/** @jsxRuntime classic */
/** @jsx jsx */
import type { FC, MouseEventHandler, ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	listItem: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		minWidth: 0,
		minHeight: '56px',
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		columnGap: token('space.150'),
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	coverLink: {
		position: 'absolute',
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
	},
	icon: {
		display: 'flex',
		flexShrink: 0,
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minWidth: 0,
		rowGap: token('space.025'),
	},
	secondary: {
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
	},
	lastModified: {
		flexShrink: 0,
		marginInlineStart: token('space.200'),
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
		whiteSpace: 'nowrap',
	},
	actions: {
		position: 'relative',
		zIndex: 1,
		flexShrink: 0,
	},
});

export type RecentWorkListItemLayoutProps = {
	url?: string;
	ariaLabel: string;
	children: ReactNode;
	icon?: ReactNode;
	secondary?: ReactNode;
	lastModified?: ReactNode;
	actions?: ReactNode;
	onClick?: MouseEventHandler<HTMLElement>;
	testId?: string;
};

export const RecentWorkListItemLayout: FC<RecentWorkListItemLayoutProps> = ({
	url,
	ariaLabel,
	children,
	icon,
	secondary,
	lastModified,
	actions,
	onClick,
	testId,
}) => (
	<div css={styles.listItem} role="listitem" data-testid={testId}>
		{url && (
			<Anchor
				xcss={styles.coverLink}
				href={url}
				aria-label={ariaLabel}
				onClick={onClick}
				draggable={false}
			/>
		)}
		{!url && onClick && (
			<Pressable xcss={styles.coverLink} aria-label={ariaLabel} onClick={onClick} />
		)}
		{icon && (
			<div css={styles.icon} data-testid="list-item-icon">
				{icon}
			</div>
		)}
		<div css={styles.content}>
			{children}
			{secondary && <div css={styles.secondary}>{secondary}</div>}
		</div>
		{lastModified && <div css={styles.lastModified}>{lastModified}</div>}
		{actions && <div css={styles.actions}>{actions}</div>}
	</div>
);
